/**
 * BatchProcessView Component
 *
 * Alternate view for the Production Timeline page showing batches as cards,
 * each with a ProcessBar visualisation of their production phases.
 *
 * Supports:
 *   - Grouping by strain
 *   - Contiguous / Non-contiguous display toggle
 *   - Phase opacity driven by batch_phases status (pending/active/completed)
 */

import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import ProcessBar from './ProcessBar'
import SpawnAllocationPopover from './SpawnAllocationPopover'
import SubstrateBatchTree from './SubstrateBatchTree'

/**
 * Status badge for batch current state
 */
function BatchStatusBadge({ status }) {
  const config = {
    // Block batch states (uppercase from production_batches.current_state)
    CREATED: { label: 'Created', className: 'bg-slate-100 text-slate-700' },
    HEAT_CYCLING: { label: 'Heat Cycling', className: 'bg-orange-100 text-orange-800' },
    COOLING: { label: 'Cooling', className: 'bg-sky-100 text-sky-800' },
    INOCULATED: { label: 'Inoculated', className: 'bg-amber-100 text-amber-800' },
    SHOOGLED: { label: 'Shoogled', className: 'bg-amber-100 text-amber-800' },
    INCUBATING: { label: 'Incubating', className: 'bg-blue-100 text-blue-800' },
    FULLY_COLONISED: { label: 'Fully Colonised', className: 'bg-emerald-100 text-emerald-800' },
    FRUITING: { label: 'Fruiting', className: 'bg-green-100 text-green-800' },
    PINNING: { label: 'Pinning', className: 'bg-green-100 text-green-800' },
    FIRST_FLUSH: { label: 'First Flush', className: 'bg-green-100 text-green-800' },
    RESTING: { label: 'Resting', className: 'bg-sky-100 text-sky-800' },
    SECOND_FLUSH: { label: 'Second Flush', className: 'bg-green-100 text-green-800' },
    SPENT: { label: 'Spent', className: 'bg-gray-100 text-gray-700' },
    CONTAMINATED: { label: 'Contaminated', className: 'bg-red-100 text-red-800' },
    QUARANTINED: { label: 'Quarantined', className: 'bg-red-100 text-red-800' },
    // Spawn batch states (lowercase from spawn_batches.status)
    colonising: { label: 'Colonising', className: 'bg-orange-100 text-orange-800' },
    colonised: { label: 'Colonised', className: 'bg-emerald-100 text-emerald-800' },
    storage: { label: 'Storage', className: 'bg-blue-100 text-blue-800' },
    used: { label: 'Used', className: 'bg-gray-100 text-gray-700' },
    contaminated: { label: 'Contaminated', className: 'bg-red-100 text-red-800' },
  }

  const cfg = config[status] || { label: status || 'Planned', className: 'bg-slate-100 text-slate-700' }
  return (
    <span className={cn('text-[11px] px-2 py-0.5 rounded-full font-medium', cfg.className)}>
      {cfg.label}
    </span>
  )
}

/**
 * Single batch card with process bar
 */
// Phase names that represent spawn production (clickable for allocation)
const SPAWN_PHASE_NAMES = new Set([
  'Spawn Production', 'Spawn Run', 'Spawn Incubation',
  'Spawn Colonisation', 'Colonisation',
])

function BatchCard({ batch, mode }) {
  const strainName = typeof batch.strain === 'string' ? batch.strain : batch.strain?.name || 'Unknown'
  const batchCode = batch.code || batch.id
  const status = batch.currentState || 'planned'
  const [spawnPopover, setSpawnPopover] = useState(null) // { position }

  // Calculate completion percentage from phases
  const completedPhases = (batch.phases || []).filter(p => p.status === 'completed').length
  const totalPhases = (batch.phases || []).length
  const completionPercent = totalPhases > 0 ? Math.round((completedPhases / totalPhases) * 100) : 0

  // Calculate total actual days so far
  const activeDays = useMemo(() => {
    const phases = batch.phases || []
    if (phases.length === 0) return null
    const firstStart = phases
      .map(p => p.actualStart || p.startDate)
      .filter(Boolean)
      .sort((a, b) => a - b)[0]
    if (!firstStart) return null
    const now = new Date()
    return Math.round((now - firstStart) / (1000 * 60 * 60 * 24))
  }, [batch.phases])

  // Only block batches can have spawn allocated (not spawn batches themselves)
  const canAllocateSpawn = batch.batchType !== 'spawn'

  const handlePhaseClick = (phase, e) => {
    if (!canAllocateSpawn) return
    if (!SPAWN_PHASE_NAMES.has(phase.name) && !SPAWN_PHASE_NAMES.has(phase.label)) return

    const rect = e.currentTarget.getBoundingClientRect()
    setSpawnPopover({
      position: {
        x: rect.left + rect.width / 2,
        y: rect.bottom,
      },
    })
  }

  return (
    <div className="p-4">
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm">{batchCode}</span>
              {batch.batchType === 'spawn' && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">
                  Spawn
                </span>
              )}
              {batch.isShadow && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium border border-dashed border-slate-300">
                  Planned
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">{strainName}</div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {activeDays !== null && (
            <span className="text-xs text-muted-foreground">
              Day {activeDays}
            </span>
          )}
          <div className="text-right">
            <BatchStatusBadge status={status} batchType={batch.batchType} />
            {totalPhases > 0 && (
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {completedPhases}/{totalPhases} phases · {completionPercent}%
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Process bar */}
      <ProcessBar
        phases={batch.phases || []}
        mode={mode}
        height={36}
        onPhaseClick={handlePhaseClick}
      />

      {/* Spawn allocation popover */}
      {spawnPopover && (
        <SpawnAllocationPopover
          batch={batch}
          position={spawnPopover.position}
          onClose={() => setSpawnPopover(null)}
          onAllocated={(spawnBatch) => {
            console.log(`[BatchProcessView] Allocated spawn ${spawnBatch.code} to batch ${batchCode}`)
          }}
        />
      )}
    </div>
  )
}

/**
 * Collapsible strain group
 */
function StrainGroup({ strainName, batches, mode, defaultExpanded = true }) {
  const [expanded, setExpanded] = useState(defaultExpanded)

  return (
    <div>
      <button
        className="flex items-center gap-2 py-2 px-1 text-sm font-semibold text-foreground hover:text-primary transition-colors w-full text-left"
        onClick={() => setExpanded(e => !e)}
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        {strainName}
        <span className="text-xs font-normal text-muted-foreground">
          ({batches.length} batch{batches.length !== 1 ? 'es' : ''})
        </span>
      </button>

      {expanded && (
        <div className="space-y-3 ml-6 mb-4">
          {batches.map(batch => (
            <BatchCard
              key={batch.id || batch.code}
              batch={batch}
              mode={mode}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * BatchProcessView — main component
 */
export default function BatchProcessView({ batches }) {
  const [displayMode, setDisplayMode] = useState('contiguous') // 'contiguous' | 'non-contiguous'
  const [filterStatus, setFilterStatus] = useState('all') // 'all' | 'active' | 'completed'

  // Filter batches
  const filteredBatches = useMemo(() => {
    if (filterStatus === 'all') return batches

    return batches.filter(batch => {
      const phases = batch.phases || []
      if (filterStatus === 'active') {
        return phases.some(p => p.status === 'active') || phases.some(p => p.status === 'pending')
      }
      if (filterStatus === 'completed') {
        return phases.length > 0 && phases.every(p => p.status === 'completed' || p.status === 'skipped')
      }
      return true
    })
  }, [batches, filterStatus])

  // Group by strain
  const strainGroups = useMemo(() => {
    const groups = new Map()
    filteredBatches.forEach(batch => {
      const strainName = typeof batch.strain === 'string'
        ? batch.strain
        : batch.strain?.name || 'Unknown Strain'
      if (!groups.has(strainName)) {
        groups.set(strainName, [])
      }
      groups.get(strainName).push(batch)
    })

    // Sort groups by strain name, then batches within each group by date
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, batchList]) => ({
        strainName: name,
        batches: batchList.sort((a, b) => {
          const dateA = a.phases?.[0]?.startDate || new Date(0)
          const dateB = b.phases?.[0]?.startDate || new Date(0)
          return dateA - dateB // planned order (earliest first)
        }),
      }))
  }, [filteredBatches])

  return (
    <div className="h-full overflow-y-auto p-4">
      {/* Controls bar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {/* Filter */}
          <div className="flex border border-border rounded-lg overflow-hidden text-sm">
            {['all', 'active', 'completed'].map(status => (
              <button
                key={status}
                className={cn(
                  'px-3 py-1.5 capitalize',
                  filterStatus === status
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                )}
                onClick={() => setFilterStatus(status)}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Display mode toggle */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Display:</span>
          <div className="flex border border-border rounded-lg overflow-hidden text-sm">
            <button
              className={cn(
                'px-3 py-1.5',
                displayMode === 'contiguous'
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted'
              )}
              onClick={() => setDisplayMode('contiguous')}
            >
              Contiguous
            </button>
            <button
              className={cn(
                'px-3 py-1.5',
                displayMode === 'non-contiguous'
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted'
              )}
              onClick={() => setDisplayMode('non-contiguous')}
            >
              Non-contiguous
            </button>
          </div>
        </div>
      </div>

      {/* Non-contiguous: substrate batch tree view */}
      {displayMode === 'non-contiguous' ? (
        <SubstrateBatchTree batches={filteredBatches} />
      ) : (
        <>
          {/* Batch count */}
          <div className="text-xs text-muted-foreground mb-3">
            {filteredBatches.length} batch{filteredBatches.length !== 1 ? 'es' : ''} across {strainGroups.length} strain{strainGroups.length !== 1 ? 's' : ''}
          </div>

          {/* Strain groups */}
          {strainGroups.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No batches match the current filter.
            </div>
          ) : (
            <div className="space-y-2">
              {strainGroups.map(({ strainName, batches: groupBatches }) => (
                <StrainGroup
                  key={strainName}
                  strainName={strainName}
                  batches={groupBatches}
                  mode="contiguous"
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
